import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { FiChrome, FiCheck, FiTrash2, FiSave, FiAlertCircle, FiCalendar } from "react-icons/fi";
import {
  getGoogleStatus, getGoogleAuthUrl, disconnectGoogle,
  getAvailability, updateAvailability,
} from "../../api/meetingsApi";
import toast from "react-hot-toast";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const TIMEZONES = [
  "Asia/Karachi",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Europe/London",
  "America/New_York",
  "America/Los_Angeles",
  "UTC",
];

export default function MeetingSettings() {
  const location = useLocation();

  const [googleStatus, setGoogleStatus] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [saving, setSaving] = useState(false);

  // Load Google Calendar connection status
  const loadStatus = useCallback(async () => {
    try {
      const s = await getGoogleStatus();
      setGoogleStatus(s);
    } catch {
      setGoogleStatus({ connected: false });
    }
  }, []);

  // Load availability settings
  const loadAvailability = useCallback(async () => {
    try {
      const data = await getAvailability();
      setAvailability(data);
    } catch {
      toast.error("Failed to load availability");
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadAvailability();
    // Check if just connected from OAuth redirect
    const params = new URLSearchParams(location.search);
    if (params.get("connected") === "true") {
      toast.success("Google Calendar connected successfully!");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [loadStatus, loadAvailability, location.search]);

  // Connect Google Calendar
  const handleConnect = async () => {
    try {
      const data = await getGoogleAuthUrl();
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error("Failed to start Google auth: " + e.message);
    }
  };

  // Disconnect Google Calendar
  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Future bookings won't generate Meet links.")) return;
    try {
      await disconnectGoogle();
      setGoogleStatus({ connected: false });
      toast.success("Google Calendar disconnected.");
    } catch (e) {
      toast.error("Failed to disconnect: " + e.message);
    }
  };

  // Update local availability state
  const updateDay = (dayIndex, field, value) => {
    setAvailability(prev => prev.map(d =>
      d.day_of_week === dayIndex ? { ...d, [field]: value } : d
    ));
  };

  // Save availability
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAvailability(availability);
      toast.success("Availability saved!");
    } catch (e) {
      toast.error("Failed to save: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const currentTimezone = availability[0]?.timezone || "Asia/Karachi";

  const updateTimezone = (tz) => {
    setAvailability(prev => prev.map(d => ({ ...d, timezone: tz })));
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Meeting Settings</h1>
        <p className="text-sm text-gray-500 mt-0.5">Configure your Google Calendar and working hours</p>
      </div>

      {/* ── Google Calendar Card ── */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <FiCalendar size={20} className="text-blue-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-800">Google Calendar</h2>
            <p className="text-xs text-gray-500">Connect to auto-create events and generate Meet links</p>
          </div>
        </div>

        {googleStatus === null ? (
          <div className="h-8 w-48 bg-gray-100 rounded animate-pulse" />
        ) : googleStatus.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
              <FiCheck size={16} className="text-green-600 shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-700">Connected</p>
                {googleStatus.calendar_email && (
                  <p className="text-xs text-green-600">{googleStatus.calendar_email}</p>
                )}
              </div>
            </div>
            <button
              onClick={handleDisconnect}
              className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition"
            >
              <FiTrash2 size={14} />
              Disconnect Google Calendar
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <FiAlertCircle size={16} className="text-amber-600 shrink-0" />
              <p className="text-sm text-amber-700">
                Not connected. Connect to enable Google Meet links for bookings.
              </p>
            </div>
            <button
              onClick={handleConnect}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition font-medium"
            >
              <FiChrome size={16} />
              Connect Google Calendar
            </button>
          </div>
        )}
      </div>

      {/* ── Timezone ── */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h2 className="font-semibold text-gray-800 mb-4">Timezone</h2>
        <select
          value={currentTimezone}
          onChange={e => updateTimezone(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {TIMEZONES.map(tz => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-2">All booking slots will be shown in this timezone to your clients.</p>
      </div>

      {/* ── Availability ── */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h2 className="font-semibold text-gray-800 mb-1">Working Hours</h2>
        <p className="text-xs text-gray-500 mb-5">Set the hours when clients can book meetings with you.</p>

        {availability.length === 0 ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-4 border-blue-600 border-r-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {DAYS.map((dayName, idx) => {
              const dayData = availability.find(d => d.day_of_week === idx);
              if (!dayData) return null;
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-4 p-3 rounded-lg transition ${
                    dayData.is_active ? "bg-blue-50" : "bg-gray-50 opacity-60"
                  }`}
                >
                  {/* Toggle */}
                  <button
                    onClick={() => updateDay(idx, "is_active", !dayData.is_active)}
                    className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${
                      dayData.is_active ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      dayData.is_active ? "translate-x-5" : "translate-x-0"
                    }`} />
                  </button>

                  {/* Day name */}
                  <span className="w-24 text-sm font-medium text-gray-700 shrink-0">{dayName}</span>

                  {/* Time range */}
                  {dayData.is_active ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="time"
                        value={dayData.start_time}
                        onChange={e => updateDay(idx, "start_time", e.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-gray-400 text-sm">to</span>
                      <input
                        type="time"
                        value={dayData.end_time}
                        onChange={e => updateDay(idx, "end_time", e.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic">Unavailable</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="mt-6 flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-60"
        >
          {saving ? (
            <><span className="w-4 h-4 border-2 border-white border-r-transparent rounded-full animate-spin" /> Saving...</>
          ) : (
            <><FiSave size={15} /> Save Availability</>
          )}
        </button>
      </div>
    </div>
  );
}
